import React from 'react';
import { CheckCircle, Users, Target, BarChart } from 'lucide-react';

const About = () => {
  const values = [
    {
      icon: Target,
      title: 'Data-Driven Strategy',
      description: 'Every decision backed by proprietary audience activation platform and competitive analysis.',
    },
    {
      icon: BarChart,
      title: 'Superior Transparency',
      description: 'Full visibility into campaign performance with real-time reporting and insights.',
    },
    {
      icon: Users,
      title: 'Expert Team',
      description: 'Industry-leading performance marketing experts dedicated to exceeding your goals.',
    },
  ];

  const capabilities = [
    'Programmatic direct media buying',
    'Multi-channel campaign execution',
    'Proprietary audience activation',
    'Real-time performance optimization',
    'Comprehensive analytics & reporting',
    'Strategic consultation & planning',
  ];

  return (
    <section id="about" className="py-24 relative">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          {/* Left Content */}
          <div className="space-y-8">
            <div>
              <div className="inline-flex items-center space-x-2 bg-cyan-500/10 border border-cyan-500/20 rounded-full px-4 py-2 backdrop-blur-sm mb-6">
                <span className="text-sm text-cyan-400 font-medium">About Applink Media</span>
              </div>
              <h2 className="text-4xl sm:text-5xl font-bold text-white mb-6">
                Industry Leaders in
                <span className="block text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-blue-500">
                  Performance Marketing
                </span>
              </h2>
              <p className="text-xl text-slate-400 leading-relaxed mb-6">
                Applink Media provides unique and targeted data-driven performance solutions to clients. Our combined capabilities in programmatic direct media buying are designed to provide superior transparency, insights, and scalable performance.
              </p>
              <p className="text-lg text-slate-400 leading-relaxed">
                We focus on creating a seamless and consistent customer experience across all channels and touchpoints. Our goal is to provide a unified and integrated brand experience to customers, regardless of how or where they interact.
              </p>
            </div>

            {/* Capabilities */}
            <div className="space-y-4">
              <h3 className="text-xl font-bold text-white">Our Capabilities</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {capabilities.map((capability, index) => (
                  <div key={index} className="flex items-start space-x-3">
                    <CheckCircle className="w-5 h-5 text-cyan-400 flex-shrink-0 mt-0.5" />
                    <span className="text-slate-300 text-sm">{capability}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Right Content - Values */}
          <div className="space-y-6">
            {values.map((value, index) => {
              const Icon = value.icon;
              return (
                <div
                  key={index}
                  className="group relative bg-slate-900/40 backdrop-blur-xl border border-slate-800/50 rounded-2xl p-8 hover:border-cyan-500/50 transition-all duration-300"
                >
                  {/* Icon */}
                  <div className="flex items-start space-x-6">
                    <div className="flex-shrink-0">
                      <div className="inline-flex items-center justify-center w-14 h-14 rounded-xl bg-cyan-500/10 border border-cyan-500/20 group-hover:bg-cyan-500/20 transition-colors duration-300">
                        <Icon className="w-7 h-7 text-cyan-400" />
                      </div>
                    </div>

                    {/* Content */}
                    <div className="space-y-2 flex-1">
                      <h3 className="text-xl font-bold text-white group-hover:text-cyan-400 transition-colors duration-300">
                        {value.title}
                      </h3>
                      <p className="text-slate-400 leading-relaxed">{value.description}</p>
                    </div>
                  </div>

                  {/* Hover Effect */}
                  <div className="absolute inset-0 rounded-2xl bg-gradient-to-br from-cyan-500/5 to-blue-500/5 opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none"></div>
                </div>
              );
            })}

            {/* CTA Box */}
            <div className="relative bg-gradient-to-br from-cyan-500/10 to-blue-500/10 backdrop-blur-xl border border-cyan-500/30 rounded-2xl p-8">
              <div className="space-y-4">
                <h3 className="text-2xl font-bold text-white">
                  Ready to Elevate Your Performance?
                </h3>
                <p className="text-slate-300 leading-relaxed">
                  Let our performance marketing experts work with you to establish your digital performance goals and create a bespoke marketing plan.
                </p>
                <button
                  onClick={() => {
                    const element = document.getElementById('contact');
                    if (element) element.scrollIntoView({ behavior: 'smooth' });
                  }}
                  className="inline-flex items-center bg-cyan-500 hover:bg-cyan-600 text-white px-6 py-3 rounded-lg font-medium transition-all duration-200"
                >
                  Start Your Journey
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default About;
